import * as React from "react";
import { cn } from "../utils";

/**
 * BoundedScroll — the ONE bounded-height scroll shell for dense, unbounded content
 * (UI-UX-PATTERNS-PRD Issue 8). A region that can hold arbitrarily many rows/cards
 * (a Board's columns, the Planner calendar, a long review inbox) must NOT grow the
 * page to N-items tall — the header, sub-nav, and filters above it would scroll away.
 * This wrapper caps the region's height to the viewport and scrolls INSIDE itself, so
 * the page chrome stays pinned and only the data region moves.
 *
 * How it works (without re-plumbing the shared single-scroll shell):
 *  · It sets a DEFINITE height anchored to the viewport — `h-[calc(100dvh - offset)]`
 *    — so an `h-full` child (the canonical `<Board>`, which is `flex h-full min-h-0`)
 *    resolves its percentage height and its own column `overflow-y-auto` /
 *    horizontal `overflow-x-auto` engage. `min-h-0` lets the flex child actually
 *    shrink below its content size (the classic flexbox scroll trap).
 *  · `overflow-hidden` on the shell keeps a Board's edge-fade + horizontal
 *    column-scroll contained (no horizontal PAGE overflow); a non-`h-full` child
 *    (a calendar grid, a plain long list) opts into `scroll` to get an internal
 *    vertical scrollbar instead of growing the page.
 *
 * The `--bounded-scroll-offset` accounts for the chrome ABOVE the region (top nav +
 * mode title + pulse strip + this sub-nav). It's intentionally generous so the region
 * never overshoots the fold on a short laptop; a tall monitor simply shows more.
 * `offset` overrides it per surface when a surface has unusually tall/short chrome.
 */
export interface BoundedScrollProps {
  children: React.ReactNode;
  /**
   * `contain` (default) — a DEFINITE-height box; use for an `h-full` child that
   * owns its own inner scroll (the canonical `<Board>`): the box just bounds it,
   * the child scrolls its columns.
   * `scroll` — the box itself scrolls vertically; use for a child that renders at
   * natural height (a calendar grid, a plain long list).
   */
  mode?: "contain" | "scroll";
  /** CSS length subtracted from `100dvh` to leave room for the chrome above. */
  offset?: string;
  className?: string;
}

/** Default vertical space reserved for the chrome above a dense region (top nav +
 *  mode title + pulse strip + sub-nav + page gutter). Tuned to keep the region
 *  comfortably above the fold on a short viewport without cropping content. */
const DEFAULT_OFFSET = "22rem";

export function BoundedScroll({
  children,
  mode = "contain",
  offset = DEFAULT_OFFSET,
  className,
}: BoundedScrollProps) {
  return (
    <div
      className={cn(
        // A definite, viewport-anchored height + `min-h-0` so an `h-full`/flex child
        // resolves and scrolls INSIDE this box rather than growing the page. A floor
        // keeps it usable on very short viewports.
        "flex min-h-[24rem] min-w-0 flex-col",
        mode === "scroll" ? "overflow-y-auto overflow-x-hidden" : "overflow-hidden",
        className,
      )}
      style={{ height: `calc(100dvh - ${offset})` }}
    >
      {children}
    </div>
  );
}
